/**
 * The four things Kimene is, on one screen.
 *
 * This is the band the home page exists for. A visitor who has read the hero
 * knows the phrase "control plane" and nothing else; what they need next is the
 * shape of the product — four claims, each one sentence, each with somewhere to
 * go for the version with evidence attached. A reader who wants none of that
 * leaves knowing what it is, which is the job.
 *
 * Every card links into a section of a deeper page rather than restating it.
 * That is the rule the whole site is arranged around: the home page says what,
 * the product and security pages say how, and no claim is made in two places
 * where it would have to be corrected in two places.
 *
 * The glyphs are drawn here rather than imported. Four line figures at 28px do
 * not justify an icon dependency, and these are the only four — the sections
 * they point at carry the real figures.
 */
import type { ReactNode } from 'react'

import { Reveal, Stagger } from '../kit'
import { useLocale, useT } from '../i18n/context'
import { pathFor, type PageId } from '../routes'
import type { MessageKey } from '../i18n'

interface Pillar {
  key: string
  title: MessageKey
  body: MessageKey
  page: PageId
  anchor: string
  glyph: ReactNode
}

/** A square line glyph. Stroked with `currentColor` so it follows the card's
 *  text colour into the light theme without a second set of values. */
function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      className="kl-pillar__glyph"
      viewBox="0 0 32 32"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

const PILLARS: Pillar[] = [
  {
    key: 'pools',
    title: 'home.pillar.pools.title',
    body: 'home.pillar.pools.body',
    page: 'product',
    anchor: 'pools',
    // Six independent nodes, unconnected: the pools do not own each other.
    glyph: (
      <Glyph>
        <rect x="3" y="4" width="11" height="7" rx="1.5" />
        <rect x="18" y="4" width="11" height="7" rx="1.5" />
        <rect x="3" y="13" width="11" height="7" rx="1.5" />
        <rect x="18" y="13" width="11" height="7" rx="1.5" />
        <rect x="3" y="22" width="11" height="6" rx="1.5" />
        <rect x="18" y="22" width="11" height="6" rx="1.5" />
      </Glyph>
    ),
  },
  {
    key: 'decision',
    title: 'home.pillar.decision.title',
    body: 'home.pillar.decision.body',
    page: 'product',
    anchor: 'decision',
    // Many in, one out, and a mark on the line: a route, and the record of it.
    glyph: (
      <Glyph>
        <path d="M4 7h7a5 5 0 0 1 5 5v8" />
        <path d="M4 16h5a3 3 0 0 1 3 3v1" />
        <path d="M4 25h4" />
        <circle cx="16" cy="22" r="3" />
        <path d="M16 25v3" />
        <path d="M22 11h6M22 16h6M22 21h4" />
      </Glyph>
    ),
  },
  {
    key: 'evidence',
    title: 'home.pillar.evidence.title',
    body: 'home.pillar.evidence.body',
    page: 'product',
    anchor: 'evidence',
    // A document with a check: completion that had to be shown.
    glyph: (
      <Glyph>
        <path d="M7 3h12l6 6v20H7z" />
        <path d="M19 3v6h6" />
        <path d="M11 18l3.5 3.5L22 14" />
      </Glyph>
    ),
  },
  {
    key: 'boundary',
    title: 'home.pillar.boundary.title',
    body: 'home.pillar.boundary.body',
    page: 'security',
    anchor: 'security',
    // A boundary with a key held on one side of it.
    glyph: (
      <Glyph>
        <path d="M16 3l11 4v9c0 7-4.6 11.6-11 13-6.4-1.4-11-6-11-13V7z" />
        <circle cx="16" cy="14" r="2.6" />
        <path d="M16 16.6V22" />
        <path d="M14 19.5h4" />
      </Glyph>
    ),
  },
]

export function Pillars() {
  const t = useT()
  const locale = useLocale()

  return (
    <section className="kl-section kl-pillars" aria-labelledby="pillars-title">
      <div className="kl-inner">
        <Reveal className="kl-head">
          <p className="k-eyebrow">
            <span className="k-label">{t('home.pillars.eyebrow')}</span>
          </p>
          <h2 id="pillars-title" className="kl-title">
            {t('home.pillars.title')}
          </h2>
          <p className="kl-sub">{t('home.pillars.sub')}</p>
        </Reveal>

        <Stagger className="kl-pillars__grid" as="ul" childAs="li" step={0.06}>
          {PILLARS.map((p) => (
            <a
              key={p.key}
              className="kl-pillar"
              href={`${pathFor(locale, p.page)}#${p.anchor}`}
            >
              {p.glyph}
              <h3 className="kl-pillar__title">{t(p.title)}</h3>
              <p className="kl-pillar__body">{t(p.body)}</p>
              <span className="kl-pillar__more" aria-hidden="true">
                {t('ui.readMore')}
              </span>
            </a>
          ))}
        </Stagger>
      </div>
    </section>
  )
}
